import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useApolloClient } from '@apollo/client';
import {
  Alert,
  Button,
  Input,
  message,
  Skeleton,
  Spin,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  ArrowLeftOutlined,
  DownloadOutlined,
  LeftOutlined,
  LoadingOutlined,
  PlayCircleOutlined,
  RightOutlined,
  ScissorOutlined,
} from '@ant-design/icons';
import {
  addClipNote,
  fetchClipRecord,
  fetchStreamRecordings,
  getPresignedUrl,
  isVideoFile,
  isThumbnailFile,
  sortedClipFiles,
} from 'api/aiClips/client';
import {
  AIClipNoteMessage,
  AIClipRecord,
  StreamRecording,
} from 'api/aiClips/types';
import { AppContext } from 'context/AppContext';
import { getFileNameWithoutExtension } from 'helpers/utils';
import { formatDuration } from 'helpers/time';
import ENV from 'api/env';
import { AI_CLIPS } from 'constants/routes';
import styles from './AIClips.module.scss';
import { formatScore, getScoreColor } from './clipScore';
import { buildSources, SourceEntry } from './recordingSources';
import SourcePlaylist from './SourcePlaylist';
import { useClipReviewTheater } from './useClipReviewTheater';

const { Text } = Typography;

interface ClipEntry {
  name: string;
  s3Key: string;
  sourceUrl: string;
  thumbUrl: string | null;
  index: number;
  title?: string;
  topic?: string;
  score?: number;
}

interface RouteParams {
  userSlug: string;
  streamSlug: string;
}

function normalizeNotesMap(
  notes: AIClipRecord['notes']
): Record<string, AIClipNoteMessage[]> {
  if (!notes) return {};

  return Object.fromEntries(
    notes
      .filter(
        (thread) =>
          thread.clipName.trim().length > 0 && thread.messages.length > 0
      )
      .map((thread) => [thread.clipName, thread.messages])
  );
}

/** Lazy-loaded sidebar thumbnail — only loads media when scrolled into view */
const LazyThumb: React.FC<{
  thumbUrl: string | null;
  clipUrl: string | undefined;
  idx: number;
  duration?: number;
}> = React.memo(function LazyThumb({ thumbUrl, clipUrl, idx, duration }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { rootMargin: '100px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div ref={ref} className={styles.clipThumb}>
      {!visible ? (
        <div className={styles.thumbPlaceholder}>
          <ScissorOutlined />
        </div>
      ) : thumbUrl ? (
        <img src={thumbUrl} alt={`Clip ${idx + 1}`} loading="lazy" />
      ) : clipUrl ? (
        <video
          src={`${clipUrl}#t=0.5`}
          preload="metadata"
          muted
          className={styles.clipThumbVideo}
        />
      ) : (
        <div className={styles.thumbPlaceholder}>
          <ScissorOutlined />
        </div>
      )}
      {duration != null && (
        <div className={styles.durationBadge}>{formatDuration(duration)}</div>
      )}
    </div>
  );
});

const AIClipDetail: React.FC = () => {
  const { userSlug, streamSlug } = useParams<RouteParams>();
  const history = useHistory();
  const apolloClient = useApolloClient();
  const { authUser } = useContext(AppContext);
  const decodedUserSlug = decodeURIComponent(userSlug);
  const decodedStreamSlug = decodeURIComponent(streamSlug);

  useClipReviewTheater();

  const [record, setRecord] = useState<AIClipRecord | null>(null);
  const [clips, setClips] = useState<ClipEntry[]>([]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [sources, setSources] = useState<SourceEntry[]>([]);
  const [selectedSourceIdx, setSelectedSourceIdx] = useState(0);
  const [sourceDurations, setSourceDurations] = useState<
    Record<number, number>
  >({});
  const [loading, setLoading] = useState(true);
  const [videoLoading, setVideoLoading] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, AIClipNoteMessage[]>>({});
  const [draftNote, setDraftNote] = useState('');
  const [resolvedClipUrls, setResolvedClipUrls] = useState<
    Record<string, string>
  >({});
  const [clipDurations, setClipDurations] = useState<Record<string, number>>(
    {}
  );

  const clipVideoRef = useRef<HTMLVideoElement>(null);
  const noteThreadEndRef = useRef<HTMLDivElement>(null);

  // ── Data loading ──
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      setRecord(null);
      setClips([]);
      setSelectedIdx(0);
      setSources([]);
      setSelectedSourceIdx(0);
      setSourceDurations({});
      setNotes({});
      setResolvedClipUrls({});
      setClipDurations({});
      try {
        const [rec, recordings] = await Promise.all([
          fetchClipRecord(apolloClient, decodedUserSlug, decodedStreamSlug),
          // Context only: without it the page still works off the clip record's video.
          fetchStreamRecordings(apolloClient, decodedStreamSlug).catch(
            (): StreamRecording[] => []
          ),
        ]);
        if (!rec) {
          setError('Record not found in DynamoDB.');
          return;
        }
        setRecord(rec);
        setNotes(normalizeNotesMap(rec.notes));

        // The stream's recordings (see GET_STREAM_RECORDINGS); the clip record's video is only
        // the fallback.
        const playlist = await Promise.all(
          buildSources(recordings, rec.videoUrl).map(async (source) => {
            // Only the clip record's own video can be an S3 URI.
            if (!source.videoUrl.startsWith('s3://')) return source;
            try {
              const url = await getPresignedUrl(apolloClient, source.videoUrl);
              return { ...source, videoUrl: url };
            } catch {
              return source;
            }
          })
        );
        setSources(playlist.filter((source) => source.videoUrl));

        if (rec.uploadedFileList?.length) {
          const sorted = sortedClipFiles(rec.uploadedFileList);
          const videoEntries = sorted.filter((f) => isVideoFile(f.name));

          const resolved: ClipEntry[] = videoEntries.map((f, idx) => {
            const stem = getFileNameWithoutExtension(f.name);
            const thumbFile = sorted.find(
              (t) => isThumbnailFile(t.name) && t.name.startsWith(stem)
            );
            return {
              name: f.name,
              s3Key: f.s3Key,
              sourceUrl: f.url,
              thumbUrl: thumbFile?.url ?? null,
              index: idx,
              title: f.title,
              topic: f.topic,
              score: f.score,
            };
          });
          setClips(resolved);

          const urlEntries = await Promise.all(
            resolved.map(async (clip) => {
              if (clip.s3Key.startsWith('s3://')) {
                try {
                  const url = await getPresignedUrl(apolloClient, clip.s3Key);
                  return [clip.name, url] as const;
                } catch {
                  return [clip.name, clip.sourceUrl] as const;
                }
              }
              return [clip.name, clip.sourceUrl] as const;
            })
          );
          setResolvedClipUrls(Object.fromEntries(urlEntries));
        }
      } catch (e: any) {
        setError(e?.message || 'Failed to load record.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [decodedUserSlug, decodedStreamSlug, apolloClient]);

  // ── Preload ALL clip videos into browser cache for instant switching ──
  useEffect(() => {
    if (!clips.length || !Object.keys(resolvedClipUrls).length) return;

    const controllers: AbortController[] = [];
    const allUrls = clips
      .map((c) => resolvedClipUrls[c.name])
      .filter(Boolean) as string[];

    // Start with adjacent clips first, then the rest
    const adjacent = [selectedIdx - 1, selectedIdx + 1]
      .filter((i) => i >= 0 && i < clips.length)
      .map((i) => resolvedClipUrls[clips[i].name])
      .filter(Boolean) as string[];

    const rest = allUrls.filter(
      (url) =>
        !adjacent.includes(url) &&
        url !== resolvedClipUrls[clips[selectedIdx]?.name]
    );

    const ordered = [...adjacent, ...rest];

    ordered.forEach((url) => {
      const ctrl = new AbortController();
      controllers.push(ctrl);
      fetch(url, { signal: ctrl.signal, mode: 'cors' }).catch(() => undefined);
    });

    return () => {
      controllers.forEach((c) => c.abort());
    };
  }, [clips, resolvedClipUrls, selectedIdx]);

  // ── Derived state ──
  const selectedClip = clips[selectedIdx] ?? null;
  const selectedClipMessages = selectedClip
    ? notes[selectedClip.name] || []
    : [];
  const selectedClipVideoUrl = selectedClip
    ? resolvedClipUrls[selectedClip.name] || selectedClip.sourceUrl
    : null;
  const selectedSource = sources[selectedSourceIdx] ?? null;

  const selectedClipName = selectedClip?.name;
  const messagesCount = selectedClipMessages.length;

  useEffect(() => {
    setDraftNote('');
  }, [selectedClipName]);

  useEffect(() => {
    requestAnimationFrame(() => {
      noteThreadEndRef.current?.scrollIntoView({
        behavior: 'auto',
        block: 'end',
      });
    });
  }, [selectedClipName, messagesCount]);

  // ── Clip selection (clean — no setTimeout hack) ──
  const handleSelectClip = useCallback(
    (idx: number) => {
      if (idx === selectedIdx) return;
      setVideoLoading(true);
      setSelectedIdx(idx);
    },
    [selectedIdx]
  );

  // ── Keyboard navigation: arrows switch clips, space play/pause ──
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;

      if (e.key === 'ArrowLeft' && selectedIdx > 0) {
        e.preventDefault();
        handleSelectClip(selectedIdx - 1);
      } else if (e.key === 'ArrowRight' && selectedIdx < clips.length - 1) {
        e.preventDefault();
        handleSelectClip(selectedIdx + 1);
      } else if (e.key === ' ' && clipVideoRef.current) {
        e.preventDefault();
        if (clipVideoRef.current.paused) {
          clipVideoRef.current.play();
        } else {
          clipVideoRef.current.pause();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedIdx, clips.length, handleSelectClip]);

  const handleDownloadClip = useCallback(
    async (clip: ClipEntry) => {
      try {
        const url = clip.s3Key?.startsWith('s3://')
          ? resolvedClipUrls[clip.name] ||
            (await getPresignedUrl(apolloClient, clip.s3Key))
          : clip.sourceUrl;

        setResolvedClipUrls((prev) => ({
          ...prev,
          [clip.name]: url,
        }));

        window.open(url, '_blank', 'noopener,noreferrer');
      } catch (e: any) {
        message.error(e?.message || 'Failed to prepare clip download.');
      }
    },
    [apolloClient, resolvedClipUrls]
  );

  const handleSaveNote = useCallback(() => {
    if (!selectedClip) return;

    const save = async () => {
      const trimmedNote = draftNote.trim();
      if (!trimmedNote) return;

      setSavingNote(true);
      try {
        const rawNotes = await addClipNote(
          apolloClient,
          decodedUserSlug,
          decodedStreamSlug,
          selectedClip.name,
          trimmedNote
        );

        setNotes(normalizeNotesMap(rawNotes));
        setDraftNote('');
        setRecord((prev) =>
          prev
            ? {
                ...prev,
                notes: rawNotes,
              }
            : prev
        );
        message.success('Note added.');
      } catch (e: any) {
        message.error(e?.message || 'Failed to add note.');
      } finally {
        setSavingNote(false);
      }
    };

    save();
  }, [
    apolloClient,
    decodedStreamSlug,
    decodedUserSlug,
    draftNote,
    selectedClip,
  ]);

  // ── Video event handlers ──
  const handleMainVideoMetadata = useCallback(
    (e: React.SyntheticEvent<HTMLVideoElement>) => {
      if (!selectedClip) return;
      const d = e.currentTarget.duration;
      if (d && isFinite(d)) {
        setClipDurations((prev) => ({ ...prev, [selectedClip.name]: d }));
      }
    },
    [selectedClip]
  );

  const handleSourceMetadata = useCallback(
    (e: React.SyntheticEvent<HTMLVideoElement>) => {
      const d = e.currentTarget.duration;
      if (d && isFinite(d)) {
        setSourceDurations((prev) => ({ ...prev, [selectedSourceIdx]: d }));
      }
    },
    [selectedSourceIdx]
  );

  const handleVideoCanPlay = useCallback(() => {
    setVideoLoading(false);
  }, []);

  const handleVideoWaiting = useCallback(() => {
    setVideoLoading(true);
  }, []);

  const handleVideoPlaying = useCallback(() => {
    setVideoLoading(false);
  }, []);

  // ── Loading / error states ──
  if (loading) {
    return (
      <div className={styles.container}>
        <Skeleton active paragraph={{ rows: 10 }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => history.push(AI_CLIPS)}
          style={{ marginBottom: 16 }}
        >
          Back to list
        </Button>
        <Alert type="error" message={error} showIcon />
      </div>
    );
  }

  return (
    <div className={styles.detailPage}>
      {/* ── Top bar ── */}
      <div className={styles.detailHeader}>
        <Tooltip title="Back to all interviews">
          <button
            className={styles.backBtn}
            onClick={() => history.push(AI_CLIPS)}
            aria-label="Back to all interviews"
          >
            <ArrowLeftOutlined />
          </button>
        </Tooltip>
        <Text className={styles.headerTitle}>
          {selectedClip?.topic
            ? `${selectedClip.topic}: ${
                selectedClip?.title || `Clip ${selectedIdx + 1}`
              }`
            : selectedClip?.title || `Clip ${selectedIdx + 1}`}
        </Text>
        <div className={styles.headerTags}>
          {sources.length > 1 && (
            <Tag color="blue">{sources.length} recordings</Tag>
          )}
          <Tag color="default">
            {record?.clipsGenerated ?? clips.length} clips
          </Tag>
          {selectedClip?.score != null && (
            <Tag color={getScoreColor(selectedClip.score)}>
              Score: {formatScore(selectedClip.score)}
            </Tag>
          )}
        </div>
      </div>

      {/* ── Main layout: fills remaining viewport ── */}
      <div className={styles.mainLayout}>
        {/* Left: Clip selector sidebar */}
        <div className={styles.clipSidebar}>
          <div className={styles.clipSidebarHeader}>
            <Text strong style={{ fontSize: 13 }}>
              Clips
            </Text>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {clips.length} total
            </Text>
          </div>
          {clips.length === 0 ? (
            <Alert
              type="info"
              message="No clips found."
              style={{ margin: 8 }}
            />
          ) : (
            <div className={styles.clipSidebarList}>
              {clips.map((clip, idx) => {
                const clipUrl = resolvedClipUrls[clip.name];
                const duration = clipDurations[clip.name];
                return (
                  <div
                    key={clip.name}
                    role="button"
                    tabIndex={0}
                    className={`${styles.clipCard} ${
                      idx === selectedIdx ? styles.clipCardSelected : ''
                    }`}
                    onClick={() => handleSelectClip(idx)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleSelectClip(idx);
                      }
                    }}
                  >
                    <LazyThumb
                      thumbUrl={clip.thumbUrl}
                      clipUrl={clipUrl}
                      idx={idx}
                      duration={duration}
                    />
                    <div className={styles.clipCardFooter}>
                      <div className={styles.clipCardMeta}>
                        <Text strong style={{ fontSize: 11 }}>
                          Clip {idx + 1}
                        </Text>
                      </div>
                      <Tooltip title="Download">
                        <Button
                          size="small"
                          type="text"
                          icon={<DownloadOutlined style={{ fontSize: 12 }} />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadClip(clip);
                          }}
                        />
                      </Tooltip>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Center: Main Review Stage */}
        <div className={styles.centerColumn}>
          <div className={styles.clipViewer}>
            <div className={styles.clipViewerBody}>
              {selectedIdx > 0 && (
                <button
                  className={styles.navPrev}
                  onClick={() => handleSelectClip(selectedIdx - 1)}
                  aria-label="Previous clip"
                >
                  <LeftOutlined />
                </button>
              )}
              {selectedIdx < clips.length - 1 && (
                <button
                  className={styles.navNext}
                  onClick={() => handleSelectClip(selectedIdx + 1)}
                  aria-label="Next clip"
                >
                  <RightOutlined />
                </button>
              )}

              {/* Video loading overlay */}
              {videoLoading && selectedClipVideoUrl && (
                <div className={styles.videoLoadingOverlay}>
                  <Spin
                    indicator={
                      <LoadingOutlined style={{ fontSize: 32 }} spin />
                    }
                  />
                </div>
              )}

              {selectedClipVideoUrl ? (
                <video
                  key={selectedClipVideoUrl}
                  ref={clipVideoRef}
                  src={selectedClipVideoUrl}
                  controls
                  autoPlay
                  playsInline
                  preload="auto"
                  className={`${styles.videoPlayerPortrait} ${
                    videoLoading ? styles.videoFadeIn : ''
                  }`}
                  aria-label={`AI highlight clip ${selectedIdx + 1}`}
                  onLoadedMetadata={handleMainVideoMetadata}
                  onCanPlay={handleVideoCanPlay}
                  onWaiting={handleVideoWaiting}
                  onPlaying={handleVideoPlaying}
                >
                  <track kind="captions" src="" srcLang="en" label="English" />
                </video>
              ) : (
                <div className={styles.noVideo}>
                  <Text type="secondary">Select a clip to preview</Text>
                </div>
              )}
            </div>
            <div className={styles.clipInfoBar}>
              <span>
                Clip {selectedIdx + 1} / {clips.length}
              </span>
              {clipDurations[selectedClip?.name ?? ''] != null && (
                <span className={styles.clipDuration}>
                  {formatDuration(clipDurations[selectedClip?.name ?? ''] ?? 0)}
                </span>
              )}
              <span className={styles.clipKeyHint}>
                Use arrow keys to navigate
              </span>
            </div>
          </div>
        </div>

        {/* Right: Context & Feedback */}
        <div className={styles.rightWorkspace}>
          <div className={styles.referenceSection}>
            <button
              className={styles.sectionHeader}
              onClick={() => {
                const interviewUrl = `${ENV.REACT_APP_WEB_PORTAL_URL}/${decodedUserSlug}/watch-stream/video/${decodedStreamSlug}`;
                window.open(interviewUrl, '_blank', 'noopener,noreferrer');
              }}
              title="Open original interview"
            >
              <PlayCircleOutlined />
              <span>Original Interview</span>
              <span className={styles.openIcon}>&#8599;</span>
            </button>
            {selectedSource ? (
              <video
                key={selectedSource.videoUrl}
                src={selectedSource.videoUrl}
                controls
                preload="metadata"
                className={styles.referenceVideo}
                aria-label={
                  selectedSource.partNumber
                    ? `Original interview part ${selectedSource.partNumber}`
                    : 'Full interview video'
                }
                onLoadedMetadata={handleSourceMetadata}
              >
                <track kind="captions" src="" srcLang="en" label="English" />
              </video>
            ) : (
              <div className={styles.noVideoSmall}>
                <Text type="secondary">No source video</Text>
              </div>
            )}

            <SourcePlaylist
              sources={sources}
              selectedIdx={selectedSourceIdx}
              onSelect={setSelectedSourceIdx}
              measuredDurations={sourceDurations}
            />
          </div>

          <div className={styles.chatPanel}>
            <div className={styles.chatHeader}>
              <Text strong style={{ fontSize: 13 }}>
                Notes & Feedback
              </Text>
              {selectedClip && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Clip {selectedIdx + 1}
                </Text>
              )}
            </div>
            <div className={styles.chatMessages}>
              {selectedClip ? (
                selectedClipMessages.length ? (
                  selectedClipMessages.map((note) => (
                    <div key={note.id} className={styles.chatBubble}>
                      <div className={styles.chatBubbleHeader}>
                        <Text strong style={{ fontSize: 11 }}>
                          {note.authorId === authUser?.id
                            ? 'You'
                            : note.authorName}
                        </Text>
                      </div>
                      <Text style={{ fontSize: 12 }}>{note.text}</Text>
                    </div>
                  ))
                ) : (
                  <Text type="secondary" className={styles.emptyNotes}>
                    No notes yet for Clip {selectedIdx + 1}
                  </Text>
                )
              ) : (
                <Text type="secondary" className={styles.emptyNotes}>
                  Select a clip to view notes
                </Text>
              )}
              <div ref={noteThreadEndRef} />
            </div>
            {selectedClip && (
              <div className={styles.chatInput}>
                <Input
                  placeholder={`Add a note on Clip ${selectedIdx + 1}…`}
                  value={draftNote}
                  onChange={(e) => setDraftNote(e.target.value)}
                  onPressEnter={(e) => {
                    if (!e.shiftKey) {
                      e.preventDefault();
                      handleSaveNote();
                    }
                  }}
                />
                <Button
                  type="primary"
                  loading={savingNote}
                  disabled={!draftNote.trim()}
                  onClick={handleSaveNote}
                >
                  Send
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIClipDetail;
